import { useMemo, useState, useCallback } from "react"
import _ from "lodash"
import { IFeature } from "interfaces"
import { useVenue } from "providers/venue"

export const useDining = (occupants) => {
  const { levels } = useVenue()
  const [category, setCategory] = useState("ALL")
  const [ordinal, setOrdinal] = useState("ALL")
  // Filtered occupant with available anchor(occupied unit or kiosk) under category
  const operatingRestaurantOccupants: IFeature[] = useMemo(
    () =>
      occupants.filter(
        ({ properties }) =>
          properties.anchor && properties.category === "restaurant"
      ),
    [occupants]
  )

  const restaurantFilteredByCategory = useMemo(
    () =>
      category === "ALL"
        ? operatingRestaurantOccupants
        : operatingRestaurantOccupants.filter(({ properties }) =>
            _.some(properties.local_categories, { id: category })
          ),
    [category, operatingRestaurantOccupants]
  )

  const restaurantFilteredByCategoryAndOrdinal = useMemo(
    () =>
      ordinal === "ALL"
        ? restaurantFilteredByCategory
        : restaurantFilteredByCategory.filter(
            ({ properties }) => properties.ordinal === ordinal
          ),
    [restaurantFilteredByCategory, ordinal]
  )

  const onChangeCategory = useCallback((id) => {
    if (id === null) return
    setCategory(id)
  }, [])

  const onChangeOrdinal = useCallback((ordinal) => {
    if (ordinal === null) return
    setOrdinal(ordinal)
  }, [])

  const restaurantSubCategories = useMemo(
    () =>
      _(operatingRestaurantOccupants)
        .map((restaurant) => restaurant.properties.local_categories)
        .compact()
        .flatten()
        .uniqBy((category) => category.id)
        .filter((category) => category.properties.parent_id !== null)
        .value(),
    [operatingRestaurantOccupants]
  )

  const levelsWithOccupant = useMemo(
    () =>
      _(restaurantFilteredByCategoryAndOrdinal)
        .uniqBy("properties.ordinal")
        .filter((item) => !_.isNil(item.properties.ordinal))
        .map(({ properties: { ordinal } }) =>
          levels.find((level) => level.properties.ordinal === ordinal)
        )
        .sortBy("properties.ordinal")
        .value(),
    [levels, restaurantFilteredByCategoryAndOrdinal]
  )

  const sortedResult = useMemo(
    () =>
      _(restaurantFilteredByCategoryAndOrdinal)
        .orderBy(
          [
            "properties.is_featured",
            "properties.featured_image",
            "properties.logo",
            "properties.name.en",
          ],
          ["desc"]
        )
        .value(),
    [restaurantFilteredByCategoryAndOrdinal]
  )

  return {
    restaurants: sortedResult,
    categories: restaurantSubCategories,
    currentCategory: category,
    levels: levelsWithOccupant,
    // Function
    onChangeCategory,
    onChangeOrdinal,
  }
}
